/**
 * The OS the client runs on (见 18 / 19): UI flavor, catalog package
 * selection. Not a runtime discriminator and not a capability — a browser on
 * macOS is still `'darwin'`.
 *
 * Same three families Node's `process.platform` uses for these hosts. iOS /
 * Android are a later axis, not members here.
 */
export type HostOs = 'darwin' | 'win32' | 'linux';

/**
 * Visual two-state used by theme tokens and Mac/Win skins (见 13).
 * Windows is the only host that takes the `'win'` flavor; Linux follows Mac,
 * matching the shell's `data-platform` default.
 */
export type HostOsThemeFlavor = 'mac' | 'win';

/**
 * Narrow an arbitrary platform string to {@link HostOs}. Anything that is
 * neither macOS nor Windows is Linux — the same assumption window chrome and
 * font stacks already make.
 */
export function normalizeHostOs(raw: string | undefined): HostOs {
  if (raw === 'darwin') {
    return 'darwin';
  }
  return raw === 'win32' ? 'win32' : 'linux';
}

/** Theme / skin two-state for `os`. Linux follows Mac (见 `boot.tsx`). */
export function hostOsThemeFlavor(os: HostOs): HostOsThemeFlavor {
  return os === 'win32' ? 'win' : 'mac';
}

/**
 * The host-stamped OS for inline UI (见 18 / 19). Reads `window.hibroHost.os`
 * only — no UA fallback, no silent `'win32'`.
 *
 * `undefined` when the shell has not stamped (tests, a page loaded outside
 * the normal entry). Callers must treat that as a missing fact, not Windows.
 */
export function readHostOs(): HostOs | undefined {
  const os = (globalThis as { hibroHost?: { os?: string } } | undefined)
    ?.hibroHost?.os;
  if (os === 'darwin' || os === 'win32' || os === 'linux') {
    return os;
  }
  return undefined;
}
